import { COMMON_TEXT, WORDS } from '@/constants';
import { useResponsive, useSimulationRouting } from '@/hooks';
import type { ICardAction, IScorecardActions } from '@/types/scoring-actions';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useCurrentUser, useGlobalStore } from '@/store';
import { useCurrentUserFeature } from '@/features/users';
import { UseSimulationScoreFeatureParams, UseSimulationScoreFeatureReturn } from './types';
import {
  cn,
  formatScorecardPrintDate,
  getScoreCardSubtitle,
} from '@/utils';
import {
  buildNextRoleplayActions,
  buildRoleplayScorecardActions,
  buildScoreHistoryEntries,
  canRetakeRoleplay,
  canStartNextRoleplay,
  getPassFailCardVisibility,
  getSimulationActionWarningMessage,
  shouldShowComplianceFlag,
  shouldShowNextSimulationCard,
  shouldShowScoreHistoryCard,
  TScoreHistoryEntry,
} from '@/domains/scoring';

/**
 * Centralized feature hook for Simulation Score component behavior.
 *
 * Purpose:
 * - Keep the component focused on rendering.
 * - Consolidate all derived state and interaction handlers in one place.
 * - Make score-page business logic easier to test and reason about.
 *
 * Notes:
 * - Logic is intentionally kept equivalent to prior in-component behavior.
 * - Memoized values are used to avoid unnecessary recalculation churn.
 */
export const useSimulationScoreFeature = ({
  certificationId,
  certificationTitle,
  className,
  createdDate,
  externalIsPrinting,
  isLoading,
  layout,
  nextSimulation,
  onRetakeRolePlayClick,
  onViewCertificateClick,
  score,
  showRetakeRolePlayButton,
  showScoreHistory,
  simulation,
  simulationId,
  simulationTitle,
}: UseSimulationScoreFeatureParams): UseSimulationScoreFeatureReturn => {
  // ==================================================
  // Environment and global dependencies
  // ==================================================

  // Responsive breakpoint helper used by heading skeleton behavior.
  const isXlUp = useResponsive({ breakpoint: 'xl', direction: 'min' });

  // Global store data used for:
  // - cert lookup during navigation handlers
  // - print state priority
  // - score history derivation
  const { certifications, isPrinting: isPrintingFromStore, scores } = useGlobalStore();

  // Routing actions used by card and header actions.
  const { navigateToDashboard, navigateToSimulation, navigateToSimulationCertificate } =
    useSimulationRouting();

  // ==================================================
  // Derived data
  // ==================================================

  // Build score history entries from store scores:
  // - same simulation only
  // - exclude current session
  // - ignore undefined scores
  // - only include items up to current completion date
  const scoreHistoryEntries: TScoreHistoryEntry[] = useMemo(() => {
    return buildScoreHistoryEntries({
      currentDateCompleted: score?.dateCompleted,
      currentRuntimeRatingCutoffs: score?.runtimeRatingCutoffs,
      currentSessionId: score?.sessionId,
      scores,
      simulationId,
    });
  }, [
    scores,
    score?.dateCompleted,
    score?.runtimeRatingCutoffs,
    score?.sessionId,
    simulationId,
  ]);

  // Current user metadata for print title/header display.
  const currentUser = useCurrentUser();
  const { currentUserFullName } = useCurrentUserFeature({ currentUser });

  // ==================================================
  // Printing state
  // ==================================================

  // `printRef` points to the page container used by PrintButton.
  const printRef = useRef<HTMLDivElement>(null);

  // Internal toggle used when print is initiated locally.
  const [internalIsPrinting, setInternalIsPrinting] = useState(false);

  // Print precedence:
  // external prop > store print state > local internal state.
  const isPrinting = externalIsPrinting ?? isPrintingFromStore ?? internalIsPrinting;

  // Local print toggle handler used by PrintButton click.
  const handlePrintButtonClick = useCallback(() => {
    setInternalIsPrinting((previousValue) => !previousValue);
  }, []);

  // ==================================================
  // Visibility rules
  // ==================================================

  // Show compliance flag only when not loading and at least one violation category exists.
  const showComplianceFlag = useMemo(() => {
    return shouldShowComplianceFlag({
      isLoading,
      score,
    });
  }, [isLoading, score]);

  // Pass/fail cards are only applicable for certification/practice simulations.
  // For other simulation types both cards stay hidden.
  const [showPassCard, showFailCard] = useMemo(() => {
    const visibility = getPassFailCardVisibility({
      scorePass: score?.pass,
      simulationType: simulation?.type,
    });

    return [visibility.showPassCard, visibility.showFailCard];
  }, [score?.pass, simulation?.type]);

  // Score history card suppression rules:
  // - hidden during printing
  // - hidden for completed certification simulations
  // - otherwise follows feature flag/prop
  const showScoreHistoryCard = useMemo(() => {
    return shouldShowScoreHistoryCard({
      isPrinting,
      showScoreHistory,
      simulation,
    });
  }, [isPrinting, showScoreHistory, simulation]);

  // Next simulation card suppression rules:
  // - hidden during printing
  // - hidden when next simulation absent
  // - hidden for completed certification context
  const showNextSimulationCard = useMemo(() => {
    return shouldShowNextSimulationCard({
      isPrinting,
      nextSimulation,
      simulation,
    });
  }, [isPrinting, nextSimulation, simulation]);

  // ==================================================
  // Header text derivations
  // ==================================================
  // Main page title defaults to generic scorecard label when scenario title is absent.
  const pageTitleText = useMemo(() => {
    if (simulationTitle) return `${simulationTitle} ${WORDS.SCORECARD}`;
    return COMMON_TEXT.SCORECARD;
  }, [simulationTitle]);

  // Subtitle text delegated to utility to preserve centralized copy rules.
  const pageSubtitleText = useMemo(() => {
    return getScoreCardSubtitle(simulation?.type ?? 'role-play', certificationTitle, score?.pass);
  }, [simulation?.type, certificationTitle, score?.pass]);

  // Human-readable print date string.
  // Returns empty string when source date is missing.
  const printDate = useMemo(() => {
    return formatScorecardPrintDate(createdDate);
  }, [createdDate]);

  // ==================================================
  // Action handlers
  // ==================================================

  // View certificate action:
  // - prefer callback override when provided
  // - otherwise route using certification id
  const handleViewCertificateClick = useCallback(() => {
    if (onViewCertificateClick) return onViewCertificateClick();
    if (!certificationId) return undefined;
    return navigateToSimulationCertificate(certificationId);
  }, [certificationId, navigateToSimulationCertificate, onViewCertificateClick]);

  // Retake role-play action with domain precondition evaluation.
  const handleRetakeRolePlayClick = useCallback(() => {
    const resolution = canRetakeRoleplay({
      certificationId,
      certifications,
      simulation,
      simulationId,
    });

    if (!resolution.ok) {
      console.warn(getSimulationActionWarningMessage(resolution.reason));
      return;
    }

    return onRetakeRolePlayClick?.(resolution.certification, resolution.simulation);
  }, [certificationId, certifications, onRetakeRolePlayClick, simulation, simulationId]);

  // Start next role-play action with domain precondition evaluation.
  const handleStartNextRolePlayClick = useCallback(() => {
    const resolution = canStartNextRoleplay({
      certificationId,
      certifications,
      nextSimulation,
    });

    if (!resolution.ok) {
      console.warn(getSimulationActionWarningMessage(resolution.reason));
      return;
    }

    return navigateToSimulation(resolution.certification, resolution.nextSimulation);
  }, [certificationId, certifications, navigateToSimulation, nextSimulation]);

  // ==================================================
  // Layout and class derivations
  // ==================================================

  // Grid column ordering/sizing by layout mode.
  const layoutClasses = useMemo(() => {
    const leftSide =
      layout === 'compact'
        ? 'order-last col-span-12'
        : 'order-last lg:order-first lg:col-span-7 xl:col-span-8 2xl:col-span-9';

    const rightSide =
      layout === 'compact' ? 'col-span-12' : 'lg:col-span-5 xl:col-span-4 2xl:col-span-3';

    return { leftSide, rightSide };
  }, [layout]);

  // Outer container classes include caller-supplied className.
  const containerClasses = useMemo(() => {
    return cn(
      'relative mx-auto flex h-full flex-col',
      layout === 'compact' ? 'w-full' : '',
      className,
    );
  }, [className, layout]);

  // ==================================================
  // Card actions
  // ==================================================

  // Compact/default next-roleplay action configuration.
  const retakeRoleplayActions: ICardAction[] = useMemo(() => {
    return buildNextRoleplayActions({
      isNextSimulationLocked: nextSimulation?.status === 'locked',
      onStartNextRoleplayClick: handleStartNextRolePlayClick,
    });
  }, [handleStartNextRolePlayClick, nextSimulation?.status]);

  // Alias preserved for semantic readability at call sites.
  const nextRoleplayActions: ICardAction[] = retakeRoleplayActions;

  // Scorecard primary/secondary action definitions.
  // Visibility class gating follows showRetakeRolePlayButton.
  const roleplayScorecardActions: IScorecardActions = useMemo(() => {
    return buildRoleplayScorecardActions({
      isRetakeRoleplayVisible: showRetakeRolePlayButton,
      onRetakeRoleplayClick: handleRetakeRolePlayClick,
    });
  }, [handleRetakeRolePlayClick, showRetakeRolePlayButton]);

  // ==================================================
  // Public feature contract
  // ==================================================

  return {
    containerClasses,
    currentUser,
    currentUserFullName,
    handlePrintButtonClick,
    handleViewCertificateClick,
    isPrinting,
    isXlUp,
    layoutClasses,
    navigateToDashboard,
    nextRoleplayActions,
    pageSubtitleText,
    pageTitleText,
    printDate,
    printRef,
    retakeRoleplayActions,
    roleplayScorecardActions,
    scoreHistoryEntries,
    showComplianceFlag,
    showFailCard,
    showNextSimulationCard,
    showPassCard,
    showScoreHistoryCard,
  };
};

export default useSimulationScoreFeature;
